import React, { useState, useEffect } from 'react';
import {
  Users,
  UserPlus,
  CalendarClock,
  ClipboardList,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Target,
  Check,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  ArrowDownUp
} from 'lucide-react';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';

const CallListSidebarV2 = ({
  tabs,
  activeTab,
  onTabChange,
  onAddTab,
  onRemoveTab,
  onReorderTabs,
  onOpenPreferences,
  isCollapsed,
  onToggle,
  canAdd = true,
  canEdit = true,
  canDelete = true,
}) => {
  const [showAddTab, setShowAddTab] = useState(false);
  const [newTabName, setNewTabName] = useState('');
  const [newTabDescription, setNewTabDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [tempTabs, setTempTabs] = useState([]);

  useEffect(() => {
    if (isReordering) {
      setTempTabs([...tabs]);
    }
  }, [isReordering, tabs]);

  const getIcon = (name) => {
    switch (name) {
      case 'allContacts': return <Users size={20} />;
      case 'newContacts': return <UserPlus size={20} />;
      case 'followUps': return <CalendarClock size={20} />;
      case 'taskDue': return <ClipboardList size={20} />;
      case 'appointmentDue': return <CalendarDays size={20} />;
      default: return <Target size={20} />;
    }
  };

  const handleRemove = (e, index, tabName) => {
    e.stopPropagation();
    Swal.fire({
      title: 'Remove Tab?',
      text: `Are you sure you want to remove "${tabName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, remove it!',
      background: '#ffffff',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-xl px-6 py-2.5 font-semibold',
        cancelButton: 'rounded-xl px-6 py-2.5 font-semibold'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        onRemoveTab(index);
      }
    });
  };

  const moveTab = (index, direction) => {
    const newTabs = [...tempTabs];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newTabs.length) {
      [newTabs[index], newTabs[newIndex]] = [newTabs[newIndex], newTabs[index]];
      setTempTabs(newTabs);
    }
  };

  const handleSaveReorder = () => {
    onReorderTabs(tempTabs);
    setIsReordering(false);
  };

  const isTabProtected = (name) => {
    return ['allContacts', 'newContacts', 'followUps', 'taskDue', 'appointmentDue'].includes(name);
  };

  return (
    <div className={`bg-white border-r border-slate-200 transition-all duration-300 flex flex-col relative ${isCollapsed ? 'w-20' : 'w-72'}`}>
      {/* Sidebar Header */}
      <div className="p-6 flex items-center justify-between border-b border-slate-100 h-[72px]">
        {!isCollapsed && (
          <span className="font-bold text-slate-800 tracking-tight">Call Categories</span>
        )}
        <div className="flex items-center gap-1">
          {!isCollapsed && !isReordering && canEdit && (
            <div className="relative">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-slate-100 text-blue-600' : 'text-slate-500 hover:bg-slate-100'}`}
              >
                <Pencil size={18} />
              </button>

              <AnimatePresence>
                {showSettings && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowSettings(false)} />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-slate-100 p-2 z-20"
                    >
                      <button
                        onClick={() => {
                          setShowSettings(false);
                          onOpenPreferences();
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-all text-sm font-semibold"
                      >
                        <LayoutGrid size={18} />
                        Call Preference
                      </button>
                      <button
                        onClick={() => {
                          setShowSettings(false);
                          setIsReordering(true);
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-all text-sm font-semibold"
                      >
                        <ArrowDownUp size={18} />
                        Reordering
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          )}

          <button
            onClick={onToggle}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors"
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>
      </div>

      {/* Tabs List */}
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1 custom-scrollbar">
        {(isReordering ? tempTabs : tabs).map((tab, index) => (
          <div key={tab.name} className="relative group">
            <button
              onClick={() => !isReordering && onTabChange(tab.name)}
              disabled={isReordering}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${activeTab === tab.name && !isReordering
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                } ${isReordering ? 'cursor-default' : ''}`}
            >
              <div className={`${activeTab === tab.name && !isReordering ? 'text-white' : 'text-slate-400 group-hover:text-blue-600'} transition-colors`}>
                {getIcon(tab.name)}
              </div>
              {!isCollapsed && (
                <div className="flex-1 flex items-center justify-between min-w-0 pr-12">
                  <span className="font-semibold truncate text-sm">{tab.text}</span>
                </div>
              )}
            </button>

            {/* Action Buttons (Absolute Positioning) */}
            {!isCollapsed && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {isReordering ? (
                  <>
                    <button
                      onClick={() => moveTab(index, -1)}
                      disabled={index === 0}
                      className="p-1.5 hover:bg-slate-200 rounded text-slate-500 disabled:opacity-30 transition-colors"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      onClick={() => moveTab(index, 1)}
                      disabled={index === tempTabs.length - 1}
                      className="p-1.5 hover:bg-slate-200 rounded text-slate-500 disabled:opacity-30 transition-colors"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </>
                ) : (
                  !isTabProtected(tab.name) && canDelete && (
                    <button
                      onClick={(e) => handleRemove(e, index, tab.text)}
                      className="p-1.5 hover:bg-rose-100 hover:text-rose-500 rounded-lg text-slate-400 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        ))}

        {/* Add New Tab Form */}
        {!isCollapsed && !isReordering && canAdd && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            {showAddTab ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-slate-50 border border-slate-100 rounded-2xl space-y-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-slate-700">Add New Tab</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowAddTab(false)}
                      className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-600 transition-colors"
                    >
                      <X size={18} />
                    </button>
                    <button
                      onClick={async () => {
                        const success = await onAddTab({ name: newTabName, description: newTabDescription, isDefault });
                        if (success) {
                          setShowAddTab(false);
                          setNewTabName('');
                          setNewTabDescription('');
                          setIsDefault(false);
                        }
                      }}
                      className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
                    >
                      <Check size={18} />
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 px-1">Name</label>
                  <input
                    type="text"
                    placeholder="Enter Name"
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                    value={newTabName}
                    onChange={(e) => setNewTabName(e.target.value)}
                    autoFocus
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 px-1">Description</label>
                  <textarea
                    placeholder="Enter Description"
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none min-h-[80px] resize-none transition-all"
                    value={newTabDescription}
                    onChange={(e) => setNewTabDescription(e.target.value)}
                  />
                </div>

                <label className="flex items-center gap-3 px-1 cursor-pointer group">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      className="peer h-5 w-5 cursor-pointer appearance-none rounded-lg border border-slate-300 bg-white checked:bg-blue-600 checked:border-blue-600 transition-all"
                      checked={isDefault}
                      onChange={(e) => setIsDefault(e.target.checked)}
                    />
                    <Check size={14} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" />
                  </div>
                  <span className="text-sm font-semibold text-slate-600 select-none group-hover:text-slate-900 transition-colors">Make Default</span>
                </label>
              </motion.div>
            ) : (
              <button
                onClick={() => setShowAddTab(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-all group border-2 border-dashed border-slate-100 hover:border-blue-200 hover:bg-blue-50/30"
              >
                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-blue-100 group-hover:text-blue-600 transition-all">
                  <Plus size={20} className="group-hover:rotate-90 transition-transform" />
                </div>
                <span className="font-bold text-sm">Add New Tab</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Reordering Footer */}
      <AnimatePresence>
        {!isCollapsed && isReordering && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            className="p-4 border-t border-slate-100 bg-slate-50 flex gap-2"
          >
            <button
              onClick={() => setIsReordering(false)}
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-white transition-all shadow-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveReorder}
              className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
            >
              Save Changes
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CallListSidebarV2;
